import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { accessGuard } from 'app/core/auth/guards/access.guard';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { superAdminGuard } from 'app/core/auth/guards/super-admin.guard';
import { LayoutComponent } from 'app/layout/layout.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to What's New
    { path: '', pathMatch: 'full', redirectTo: 'analytics/updates' },

    // Redirect signed-in user to What's New
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'analytics/updates' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/modules/auth/confirmation-required/confirmation-required.routes'
                    ),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.routes'
                    ),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.routes'
                    ),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.routes'),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import('app/modules/auth/sign-up/sign-up.routes'),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.routes'),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.routes'
                    ),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.routes'),
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            layout: 'eastel-2',
        },
        children: [
            {
                path: 'access-denied',
                loadComponent: () =>
                    import(
                        'app/modules/admin/access-denied/access-denied.component'
                    ).then((m) => m.AccessDeniedComponent),
            },
            {
                path: 'analytics',
                canActivate: [accessGuard('analytics')],
                loadChildren: () =>
                    import('app/modules/admin/analytics/analytics.routes'),
            },
            {
                path: 'incentives',
                canActivate: [accessGuard('incentives')],
                loadChildren: () =>
                    import('app/modules/admin/incentives/incentives.routes'),
            },
            {
                path: 'organization',
                canActivate: [accessGuard('organization')],
                loadChildren: () =>
                    import('app/modules/admin/management/management.routes'),
            },
            {
                path: 'configuration',
                canActivate: [accessGuard('configuration')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/configuration/membership-configuration.routes'
                    ),
            },
            {
                path: 'app-management',
                loadChildren: () =>
                    import(
                        'app/modules/admin/app-management/app-management.routes'
                    ),
            },
            {
                path: 'messaging',
                loadChildren: () =>
                    import(
                        'app/modules/admin/app-management/mobile-app/campaign/campaign.routes'
                    ),
            },
            {
                path: 'subscriber',
                canActivate: [accessGuard('subscribers')],
                loadChildren: () =>
                    import('app/modules/admin/subscriber/subscriber.routes'),
            },
            {
                path: 'site-administration',
                canActivate: [superAdminGuard],
                loadChildren: () =>
                    import(
                        'app/modules/admin/site-administration/site-administration.routes'
                    ),
            },
            {
                path: 'settings',
                loadChildren: () =>
                    import('app/modules/admin/settings/settings.routes'),
            },
            {
                path: 'pos',
                canActivate: [accessGuard('manage-pos')],
                loadChildren: () => import('app/modules/admin/pos/pos.routes'),
            },
            {
                path: 'products',
                canActivate: [accessGuard('products')],
                loadChildren: () =>
                    import('app/modules/admin/product/product.routes'),
            },
            {
                path: 'inventory/stock-in',
                canActivate: [accessGuard('manage-inventory')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/inventory/stock-in/stock-in.routes'
                    ),
            },
            {
                path: 'inventory/pin-management',
                canActivate: [accessGuard('manage-inventory')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/inventory/pin-management/pin-management.routes'
                    ),
            },
            {
                path: 'inventory/stock-transfer',
                canActivate: [accessGuard('manage-inventory')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/stock-transfer/stock-transfer.routes'
                    ),
            },
            {
                path: 'inventory/delivery-orders',
                canActivate: [accessGuard('manage-inventory')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/delivery-order/delivery-order.routes'
                    ),
            },
            {
                path: 'inventory/orders',
                canActivate: [accessGuard('manage-inventory')],
                loadChildren: () =>
                    import('app/modules/admin/order/order.routes'),
            },
            {
                path: 'inventory/movements',
                canActivate: [accessGuard('manage-inventory')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/inventory/inventory-movement.routes'
                    ),
            },
            {
                path: 'inventory/convert-sku',
                canActivate: [accessGuard('manage-inventory')],
                loadComponent: () =>
                    import(
                        'app/modules/admin/inventory/convert-sku/convert-sku.component'
                    ).then((m) => m.ConvertSkuComponent),
            },
            {
                path: 'inventory/msisdn-lookup',
                canActivate: [accessGuard('manage-inventory')],
                loadComponent: () =>
                    import(
                        'app/modules/admin/inventory/msisdn-lookup/msisdn-lookup.component'
                    ).then((m) => m.MsisdnLookupComponent),
            },
            {
                path: 'inventory/imsi-lookup',
                canActivate: [accessGuard('manage-inventory')],
                loadComponent: () =>
                    import(
                        'app/modules/admin/inventory/imsi-lookup/imsi-lookup.component'
                    ).then((m) => m.ImsiLookupComponent),
            },
            {
                path: 'transactions',
                canActivate: [accessGuard('transactions')],
                loadChildren: () =>
                    import('app/modules/admin/transaction/transaction.routes'),
            },
        ],
    },
];
